import api from "@/api";
import { defineStore } from "pinia";
import { Rollover } from "@/interfaces/rollover.interface";
import { useLearnerStore } from "@/stores/learnerStore";
import { useFilterStore } from "@/stores/filterStore";
import { Learner } from "@/interfaces/learner.interface";

export const useRolloverStore = defineStore({
  id: "rolloverStore",
  state: () => ({
    loader: {
      rollover: false,
    },
    rollover: null as Rollover | null,
    allRollovers: null as Rollover[] | null,
  }),
  getters: {
    getRollover(): Rollover | null {
      return this.rollover;
    },
    hasActiveRollover(): boolean {
      return this.rollover !== null && this.rollover.isComplete === false;
    },
    allLearnersAssignedBeforeRollover(): boolean {
      const learnerStore = useLearnerStore();
      const allLearners = learnerStore.getAllActiveSchoolLearners;

      if (allLearners.length !== this.rollover?.learnerAssignmentActions.length) {
        // Loop through allLearners and if they have two distinct subjects in assessmentStates then check that they have the same number of classes in the rollover.learnerAssignmentActions
        
        const learnersWithClasses = allLearners.filter(
          (learner: Learner) => {
            const uniqueSubjects = Array.from(new Set(learner.assessmentStates.map((as) => as.subject)));
            const numberOfClasses = learner.classes ? learner.classes.length : 0;

            return uniqueSubjects.length === numberOfClasses;
          }
        );

        if (learnersWithClasses.length === allLearners.length) {
          // Add the learners to the rollover.learnerAssignmentActions
          const learnerAssignmentActions = allLearners.map((learner: Learner) => {
            return {
              learner: learner._id,
              newClassGroups: learner.classes.map((c) => c._id),
            };
          });

          if (this.rollover) {
            // Update the existing learnerAssignmentActions and if learner already exists then don't add them
            this.rollover.learnerAssignmentActions = this.rollover.learnerAssignmentActions.concat(
              learnerAssignmentActions.filter((learner: any) => {
                return !this.rollover?.learnerAssignmentActions.some((la) => la.learner === learner.learner);
              })
            );
          }

          return true;
        } else {
          allLearners.filter((learner: Learner) => {
            const uniqueSubjects = Array.from(new Set(learner.assessmentStates.map((as) => as.subject)));
            const numberOfClasses = learner.classes ? learner.classes.length : 0;

            return uniqueSubjects.length !== numberOfClasses;
          });
        }

        return false;
      } else {
        return true;
      }
    },
    allLearnersAssigned(): boolean {
      // Check if all learners in the schoolStore.getAllActiveSchoolLearners are in the rollover.learnerAssignmentActions and have been assigned to classes
      const learnerStore = useLearnerStore();
      const allLearners = learnerStore.getAllActiveSchoolLearners;
      const rolloverLearners = this.rollover?.learnerAssignmentActions;

      if (rolloverLearners) {
        return allLearners.every((learner: any) => {
          return rolloverLearners.some((learnerAssignment) => {
            return learner._id === learnerAssignment.learner;
          });
        });
      } else {
        return false;
      }
    },
    allLearnersAssignedForSelectedGrade(): boolean {
      // Check if all learners in the schoolStore.getAllActiveSchoolLearners are in the rollover.learnerAssignmentActions and have been assigned to classes
      const learnerStore = useLearnerStore();
      const allLearners = learnerStore.getAllActiveSchoolLearners;
      const rolloverLearners = this.rollover?.learnerAssignmentActions;

      const filterStore = useFilterStore();
      const selectedGrade = filterStore.selectedGrade;
      const filteredLearners = allLearners.filter(
        (learner: any) => learner.grade === selectedGrade
      );

      if (rolloverLearners) {
        return filteredLearners.every((learner: any) => {
          return rolloverLearners.some((learnerAssignment) => {
            return learner._id === learnerAssignment.learner;
          });
        });
      } else {
        return false;
      }
    },
  },
  actions: {
    async fetchSchoolRollover(schoolId: string) {
      this.loader.rollover = true;

      try {
        const rolloverResponse = await api.get(
          `v2/organisation/rollover/school/${schoolId}`
        );

        if (rolloverResponse.status !== 200) {
          //throw new Error("Failed to rollover data from API.");
          return false;
        }

        const rolloverData = rolloverResponse.data.data;
        this.rollover = rolloverData;
      } catch (error: any) {
        console.log("Error: ", error);

        if (error.code) {
          //throw new Error(error.message);
          this.rollover = null;

          return false;
        }
      } finally {
        this.loader.rollover = false;
      }
    },
    async updateRolloverObject(rolloverObject: Rollover) {
      this.loader.rollover = true;

      // remove updateAt and createdAt
      delete rolloverObject.updatedAt;
      delete rolloverObject.createdAt;

      try {
        // /v2/organisation/rollover/update/65c1cc797c84b3bd237ea6e1
        const rolloverResponse = await api.patch(
          `v2/organisation/rollover/update/${rolloverObject._id}`,
          rolloverObject
        );

        if (rolloverResponse.status !== 200) {
          throw new Error("Failed to rollover data from API.");
        }

        const rolloverData = rolloverResponse.data.data;
        this.rollover = rolloverData;
      } catch (error: any) {
        console.log("Error: ", error);

        if (error.code) {
          throw new Error(error.message);
        }
      } finally {
        this.loader.rollover = false;
      }
    },
    async runAssignLearnersJob(rolloverId: string) {
      this.loader.rollover = true;

      try {
        const rolloverResponse = await api.post(
          `v2/organisation/rollover/assign-learners/${rolloverId}`
        );

        if (rolloverResponse.status !== 200) {
          throw new Error("Failed to rollover data from API.");
        }

        const rolloverData = rolloverResponse.data.data;
        this.rollover = rolloverData;
      } catch (error: any) {
        console.log("Error: ", error);

        if (error.code) {
          throw new Error(error.message);
        }
      } finally {
        this.loader.rollover = false;
      }
    },
    addLearnerToLearnerAssignmentActions(learner: Learner) {
      if (this.rollover) {
        const learnerAssignmentActions = this.rollover.learnerAssignmentActions;
        const learnerIndex = learnerAssignmentActions.findIndex(
          (la) => la.learner === learner._id
        );

        if (learnerIndex === -1) {
          this.rollover.learnerAssignmentActions.push({
            learner: learner._id,
            newClassGroups: learner.classes.map((c) => c._id),
          });
        }
      }
    },
    async fetchAllRollovers() {
      this.loader.rollover = true;

      try {
        const rolloversResponse = await api.get(
          `v2/organisation/rollover/all`
        );

        if (rolloversResponse.status !== 200) {
          throw new Error("Failed to rollover data from API.");
        }

        const rolloversData = rolloversResponse.data.data;
        this.allRollovers = rolloversData;
      } catch (error: any) {
        console.log("Error: ", error);

        if (error.code) {
          throw new Error(error.message);
        }
      } finally {
        this.loader.rollover = false;
      }
    },
  },
  persist: true,
});
